import { fetchGraphql } from './util'
import {
  Currency,
  CountryIsoCode,
  isInternationalCountryIsoCode
} from '@toasttab/buffet-pui-country-utilities'
import { Locale } from '@toasttab/buffet-pui-locale-utilities'
import { useQuery } from 'react-query'

const getRestaurantQuery = () => `
  query getRestaurantQuery($restaurantId: ID!) {
    restaurantV2(guid: $restaurantId) {
      ...on Restaurant {
        guid
        i18n {
          country
          currency
          locale
        }
        timeZoneId
      }
    }
  }
`

interface I18n {
  currency: Currency
  locale: Locale
  country: CountryIsoCode
}

interface Restaurant {
  guid: string
  i18n?: I18n
  timeZoneId?: string
}

const fetchRestaurantGraphql = async (
  restaurantGuid: string
): Promise<{ data: { restaurantV2: Restaurant } }> => {
  return fetchGraphql(getRestaurantQuery(), {
    restaurantId: restaurantGuid
  }).then((r) => r.json())
}

export interface IntlRxProps {
  timeZoneId: string | undefined
  isIntlRx: boolean
  isRxGraphqlError: boolean
  isRxGraphqlLoading: boolean
  currency?: Currency
  locale?: Locale
  country?: CountryIsoCode
}

export const useIsIntlRestaurant = (
  restaurantGuid: string | undefined
): IntlRxProps => {
  const {
    data: restaurant,
    isError: isRxGraphqlError,
    isLoading: isRxGraphqlLoading
  } = useQuery(
    ['restaurant-graphql', restaurantGuid],
    () =>
      fetchRestaurantGraphql(restaurantGuid!)
        .then((resp) => resp.data.restaurantV2)
        .catch((err) => {
          console.error('Failed to load restaurant from graphql', err.message)
          throw err
        }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: !!restaurantGuid
    }
  )

  const isIntlRx = isInternationalCountryIsoCode(restaurant?.i18n?.country)

  return {
    ...restaurant?.i18n,
    timeZoneId: restaurant?.timeZoneId,
    isIntlRx,
    isRxGraphqlError,
    isRxGraphqlLoading
  }
}
